import { useEffect, useMemo, useState } from 'react';

export interface HeroCharacterPortrait {
  /** Stable identifier for React keying (e.g. `${adventureId}:${characterId}`). */
  key: string;
  /** Absolute URL of the neutral portrait. */
  url: string;
  /** Used as alt text for screen readers; the gallery itself is aria-hidden. */
  name: string;
}

interface HeroCharacterGalleryProps {
  /** Pool of portraits to draw from — typically the initial character + cast
   *  preview of every published adventure. The component dedupes and shuffles. */
  portraits: HeroCharacterPortrait[];
  /** How long each slide stays visible before fading to the next. */
  intervalMs?: number;
  /** How many distinct slides to cycle through (5 by default — enough variety
   *  without making the hero feel busy). */
  slideCount?: number;
  /** Duration of the crossfade between slides, in milliseconds. */
  fadeMs?: number;
}

/**
 * Full-width crossfading slideshow of large character portraits used as the
 * background of the catalog hero (`game.selna.ai/`).
 *
 * Design notes:
 *  - Few, big portraits (not a busy mosaic) — the hero stays calm.
 *  - Long crossfade + slow Ken Burns drift on the active slide → cinematic.
 *  - Solid dark vignette, no fade to cream → the hero is a contained dark slab
 *    sitting cleanly above the cream catalog section below.
 *  - Respects `prefers-reduced-motion`: no auto-advance, no drift.
 *  - Pure decoration — aria-hidden.
 */
export default function HeroCharacterGallery({
  portraits,
  intervalMs = 6500,
  slideCount = 5,
  fadeMs = 1500,
}: HeroCharacterGalleryProps) {
  const slides = useMemo(
    () => pickSlides(portraits, slideCount),
    [portraits, slideCount],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Track reduced-motion preference once on mount.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
  }, []);

  // Auto-advance, unless the user prefers reduced motion.
  useEffect(() => {
    if (slides.length <= 1 || prefersReducedMotion) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [slides.length, intervalMs, prefersReducedMotion]);

  if (slides.length === 0) return null;

  return (
    <div className="absolute inset-0 overflow-hidden bg-dark" aria-hidden="true">
      {/* Slides — drift animation runs on ALL of them with staggered negative
          delays so each slide is at a different phase of the 22s cycle. When
          a slide fades into view, the drift is already mid-flight: no visible
          "reset". */}
      {slides.map((slide, idx) => {
        const isActive = idx === activeIndex;
        const driftDelaySeconds = -(idx * 4);

        return (
          <div
            key={`${slide.key}-${idx}`}
            className={`absolute inset-0 transition-opacity ease-in-out ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDuration: `${fadeMs}ms` }}
            aria-hidden="true"
          >
            <img
              src={slide.url}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover object-top ${
                prefersReducedMotion ? '' : 'animate-hero-drift'
              }`}
              style={{ animationDelay: `${driftDelaySeconds}s` }}
              loading={idx === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </div>
        );
      })}

      {/* Horizontal fade — left side keeps the face visible, right side fades
          to dark so the text has a calm, dark canvas behind it. */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent from-25% via-transparent via-45% to-dark/95" />
      {/* Vertical vignette for top/bottom legibility. */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark/65 via-dark/25 to-dark/65" />
    </div>
  );
}

/**
 * Pick a stable random subset of the portrait pool.
 *
 *  - Dedupe by URL so the same face never appears twice in the slideshow.
 *  - Fisher-Yates shuffle, returning a fresh array so callers don't mutate props.
 *  - If `count` exceeds the pool size, returns the whole pool (no cycling).
 */
function pickSlides(
  pool: HeroCharacterPortrait[],
  count: number,
): HeroCharacterPortrait[] {
  if (pool.length === 0 || count <= 0) return [];

  const seen = new Set<string>();
  const deduped: HeroCharacterPortrait[] = [];
  for (const p of pool) {
    if (!p.url || seen.has(p.url)) continue;
    seen.add(p.url);
    deduped.push(p);
  }
  if (deduped.length === 0) return [];

  const shuffled = [...deduped];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, Math.min(count, shuffled.length));
}
